import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, switchMap, of } from 'rxjs';
import { environment } from '@biletix/env';
import { RegionService } from '@biletix/core/services/region.service';
import { RouterService } from '@biletix/core/services/router.service';
import { SearchService } from '@biletix/core/services/search.service';
import { ApiResponseStatus } from '@biletix/core/models/api-response-status';
import { SuggestionItem, Spellcheck } from '@biletix/core/models/search/search-suggestion.model';

@Component({
  standalone: false,
  selector: 'btx-event-search-box',
  templateUrl: './event-search-box.component.html',
  styleUrls: ['./event-search-box.component.scss'],
})
export class EventSearchBoxComponent implements OnInit {
  searchControl: FormControl = new FormControl();
  host: string = environment.biletixWebHost;
  regionAndLanguagePath: string | any;

  @ViewChild('searchKey', { static: true }) searchKeyElement: ElementRef;

  groupSuggestions: SuggestionItem[] = [];
  nameSuggestions: SuggestionItem[] = [];
  venueSuggestions: SuggestionItem[] = [];
  spellcheck: Spellcheck | null = null;
  hasSearched = false;

  constructor(
    keyElement: ElementRef,
    private regionService: RegionService,
    private routerService: RouterService,
    private searchService: SearchService
  ) {
    this.searchKeyElement = keyElement;
  }

  get hasSuggestions(): boolean {
    return this.groupSuggestions.length > 0 || this.nameSuggestions.length > 0 || this.venueSuggestions.length > 0;
  }

  ngOnInit() {
    this.regionAndLanguagePath = this.routerService.regionLanguagePath;

    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((value) => {
          if (!value || value.length <= 2) {
            this.clearSuggestions();
            return of(null);
          }
          return this.searchService.suggestions(value);
        })
      )
      .subscribe({
        next: (response) => {
          if (!response) return;
          this.hasSearched = true;
          if (response.status === ApiResponseStatus.SUCCESS) {
            console.log('suggestions raw:', response.data.suggestions);
            this.groupSuggestions = response.data.suggestions.groupName;
            this.nameSuggestions = response.data.suggestions.name;
            this.venueSuggestions = response.data.suggestions.venue ?? [];
            this.spellcheck = response.data.spellcheck ?? null;
          } else {
            this.clearSuggestions();
          }
        },
        error: () => this.clearSuggestions(),
      });
  }

  onOptionSelected(label: string) {
    this.searchKeyElement.nativeElement.value = label;
    this.goSearch();
  }

  goSearch() {
    const keyword = this.searchKeyElement.nativeElement.value;
    this.routerService.navigateToSearchPage(keyword);
  }

  get spellcheckSuggestions(): string[] {
    if (!this.spellcheck || this.spellcheck.correctlySpelled) return [];
    if (this.spellcheck.collations.length > 0) return this.spellcheck.collations;
    return this.spellcheck.suggestions.slice(0, 5);
  }

  private clearSuggestions() {
    this.groupSuggestions = [];
    this.nameSuggestions = [];
    this.venueSuggestions = [];
    this.spellcheck = null;
    this.hasSearched = false;
  }
}
